import { InputFrame } from '../sim/input';

const RISE_RATE = 4;   // axis units per second toward a held key
const RETURN_RATE = 8; // axis units per second back toward centre

/** Lever rates (units per second): a hand rolls the throttle open / squeezes a brake in ~0.2 s, lets go faster. */
export const LEVER_RISE = 5;
export const LEVER_FALL = 12;

/** A key-driven lever: ramps toward 1 while held, back to 0 when released. */
export function rampLever(current: number, held: boolean, dt: number): number {
  return held ? Math.min(1, current + LEVER_RISE * dt) : Math.max(0, current - LEVER_FALL * dt);
}

/** Moves an axis toward its target: slowly when pushing out, faster when coming back or reversing. */
export function rampAxis(current: number, target: number, dt: number): number {
  const returning = target === 0 || Math.sign(target) !== Math.sign(current);
  const step = (returning && current !== 0 ? RETURN_RATE : RISE_RATE) * dt;
  if (Math.abs(target - current) <= step) return target;
  return current + Math.sign(target - current) * step;
}

/** -1 / 0 / +1 from two key groups (either key of a group counts). */
export const keyAxis = (down: ReadonlySet<string>, neg: readonly string[], pos: readonly string[]): number =>
  (pos.some((k) => down.has(k)) ? 1 : 0) - (neg.some((k) => down.has(k)) ? 1 : 0);

/**
 * Default scheme (`beginner`, owner decision A): W throttle, S the one brake (the assist splits it front/rear with
 * ABS), A/D (or the side arrows) steer, Q or Down leans back, E or Up leans forward; the lean is also the trick key
 * (lean back + gas = wheelie, lean forward + brake = stoppie, in the air it pitches the bike: src/sim/assist.ts).
 * Stance and foot are automatic.
 *
 * Pro controls (advanced): W throttle, S rear brake, Shift front brake (the levers ramp in about 0.2 s, like a hand), A/D steer, Q/E body back/forward (a held key moves the rider there,
 * letting go brings him back to the centre), the rider sits and stands by himself (MX physics row 1) and a held Space flips that, F holds the inner foot out, R requests a reset, C cycles the camera.
 */
export class KeyboardInput {
  private down = new Set<string>();
  private resetRequested = false;
  private cameraRequested = false;
  private steer = 0;
  private body = 0;
  private gas = 0;
  private front = 0;
  private rear = 0;
  /** The default (assisted) scheme; false = Pro controls. Set from the settings. */
  beginner = false;

  constructor(target: Window) {
    target.addEventListener('keydown', (e) => {
      if (e.code === 'KeyR' && !e.repeat) this.resetRequested = true;
      if (e.code === 'KeyC' && !e.repeat) this.cameraRequested = true;
      this.down.add(e.code);
    });
    target.addEventListener('keyup', (e) => this.down.delete(e.code));
  }

  /** True once per R press. */
  takeReset(): boolean {
    const r = this.resetRequested;
    this.resetRequested = false;
    return r;
  }

  /** True once per C press. */
  takeCamera(): boolean {
    const r = this.cameraRequested;
    this.cameraRequested = false;
    return r;
  }

  /** Call once per physics step; steer and body ramp instead of jumping. */
  read(dt: number): InputFrame {
    this.steer = rampAxis(this.steer, keyAxis(this.down, ['KeyD', 'ArrowRight'], ['KeyA', 'ArrowLeft']), dt);
    this.body = rampAxis(this.body, keyAxis(this.down, ['KeyQ', 'ArrowDown'], ['KeyE', 'ArrowUp']), dt);
    // Keys are on/off; the levers behind them move like a hand does (rampLever), so a tap is a blip, not a slam.
    this.gas = rampLever(this.gas, this.down.has('KeyW'), dt);
    this.rear = rampLever(this.rear, this.down.has('KeyS'), dt);
    this.front = rampLever(this.front, this.down.has('ShiftLeft') || this.down.has('ShiftRight'), dt);
    const base = { throttle: this.gas, steer: this.steer, body: this.body, standing: false, autoPosture: true };
    if (this.beginner) return { ...base, brake: Math.max(this.rear, this.front), autoFoot: true, assist: true };
    return { ...base, standing: this.down.has('Space'), brake: 0, frontBrake: this.front, rearBrake: this.rear, foot: this.down.has('KeyF') };
  }

  reset(): void {
    this.steer = 0;
    this.body = 0;
    this.gas = this.front = this.rear = 0;
  }
}
